import { useState, useEffect, useCallback } from 'react';
import { useRealTimeOdds } from './useRealTimeOdds';

interface MarketLiveOdds {
  yesPrice: number;
  noPrice: number;
  isLoading: boolean;
  hasLiveData: boolean;
  error: string | null;
}

/**
 * Hook to get live odds for a specific market
 * Returns live data if available, fallback to provided odds
 */
export function useMarketLiveOdds(marketId: string, fallbackYesPrice: number): MarketLiveOdds {
  const [odds, setOdds] = useState<MarketLiveOdds>({
    yesPrice: fallbackYesPrice,
    noPrice: 100 - fallbackYesPrice,
    isLoading: true, // Start as loading to show blinking skeleton
    hasLiveData: false,
    error: null
  });

  const { 
    yesPrice: liveYesPrice, 
    noPrice: liveNoPrice, 
    isLoading, 
    error 
  } = useRealTimeOdds(marketId);

  useEffect(() => {
    if (liveYesPrice !== undefined && liveNoPrice !== undefined) {
      // Live data available - show live odds
      setOdds({
        yesPrice: liveYesPrice,
        noPrice: liveNoPrice,
        isLoading: false,
        hasLiveData: true,
        error: null
      });
    } else if (error) {
      // Error occurred, use fallback odds
      setOdds({
        yesPrice: fallbackYesPrice,
        noPrice: 100 - fallbackYesPrice,
        isLoading: false,
        hasLiveData: false,
        error: error
      });
    } else if (isLoading) {
      // Still loading - show initial odds with blinking skeleton
      setOdds(prev => ({
        yesPrice: fallbackYesPrice, // Show initial odds from subgraph
        noPrice: 100 - fallbackYesPrice,
        isLoading: true, // Keep loading state for blinking
        hasLiveData: false,
        error: null
      }));
    }
  }, [liveYesPrice, liveNoPrice, isLoading, error, fallbackYesPrice]);

  return odds;
}
